import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Progress } from './progress';
import { Particles } from './particles';
import { useTheme } from '@/context/ThemeContext';
import confetti from 'canvas-confetti';

interface FullscreenProgressProps {
  status: 'pending' | 'processing';
  title: string;
  subtitle: string;
  steps: string[];
  stepDuration?: number; // milliseconds between step changes
  progressIncrement?: { min: number; max: number }; // min and max progress increment
  progressInterval?: number; // milliseconds between progress updates
  completionThreshold?: number; // progress percentage to show completion message
  onComplete?: () => void;
}

export const FullscreenProgress: React.FC<FullscreenProgressProps> = ({ 
  status, 
  title,
  subtitle,
  steps,
  stepDuration = 3000,
  progressIncrement = { min: 0.5, max: 2.5 },
  progressInterval = 1000,
  completionThreshold = 98,
  onComplete
}) => {
  const { theme } = useTheme();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [color, setColor] = useState(theme === 'dark' ? '#ffffff' : '#000000');
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    setColor(theme === 'dark' ? '#ffffff' : '#000000');
  }, [theme]);
  
  useEffect(() => {
    // Start progress animation
    const interval = setInterval(() => {
      setProgress(prev => {
        const increment = Math.random() * (progressIncrement.max - progressIncrement.min) + progressIncrement.min;
        const newProgress = Math.min(prev + increment, completionThreshold);
        
        if (newProgress >= completionThreshold) {
          clearInterval(interval);
        }
        
        return newProgress;
      });
    }, progressInterval);
    
    return () => clearInterval(interval);
  }, [progressIncrement, progressInterval, completionThreshold]);
  
  useEffect(() => {
    // Change steps periodically
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [steps.length, stepDuration]);
  
  useEffect(() => {
    if (progress >= 100 && !isComplete) {
      setIsComplete(true);
      triggerConfetti();
      if (onComplete) {
        setTimeout(onComplete, 500);
      }
    }
  }, [progress, isComplete, onComplete]);
  
  const triggerConfetti = () => {
    const duration = 3 * 1000;
    const end = Date.now() + duration;
    
    const colors = ['#4F46E5', '#818CF8', '#C7D2FE', '#34D399', '#F472B6'];
    
    (function frame() {
      confetti({
        particleCount: 7,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 7,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  };
  
  return (
    <div className="absolute inset-0 bg-white dark:bg-zinc-950 z-40 flex flex-col overflow-hidden">
      <Particles
        className="absolute inset-0"
        quantity={300}
        ease={80}
        color={color}
        staticity={50}
        size={0.3}
      />
      <div className="relative z-10 flex-1 flex flex-col justify-center px-8 py-12">
        <div className="max-w-2xl w-full mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
            className="mb-12"
          >
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {subtitle}
            </p>
          </motion.div>
          
          <motion.p 
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-lg text-gray-700 dark:text-gray-300 mb-12 min-h-[60px] flex items-center justify-center px-4"
          >
            {steps[currentStep]}
          </motion.p>
          
          <div className="w-full max-w-xl mx-auto mb-12">
            <Progress value={progress} className="h-4" />
            <div className="flex justify-between items-center mt-6">
              <p className="text-base text-gray-500 dark:text-gray-400">
                {progress.toFixed(0)}% Complete
              </p>
              <p className="text-base text-gray-500 dark:text-gray-400">
                {status === 'pending' ? 'Queued' : 'Processing'}
              </p>
            </div>
          </div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="space-y-3"
          >
            <p className="text-base text-gray-600 dark:text-gray-400">
              💡 Our AI is working hard to deliver the best results
            </p>
            <p className="text-base text-gray-600 dark:text-gray-400">
              This process typically takes a few minutes
            </p>
          </motion.div>
          
          {progress >= completionThreshold && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                Almost there! Putting the finishing touches...
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};